<template>
  <div v-if="display === 'release'">
    <div class="page-block__inner">
      <release-step3-confirm
        v-if="showConfirmation || readonly"
        :country="country"
        :status="status"
        :selected-providers="selectedProviders"
        :released-providers="releasedProviders"
        :provider-schedules="providerSchedules"
        :simultaneous-release="simultaneousRelease"
        :providers="providers"
        :package-price="savedPackagePrice"
        :single-price="singlePrice"
        :hi-res-package-price="savedHiResPackagePrice"
        :hi-res-single-price="hiResSinglePrice"
        :is-hi-res="isHiRes"
        :published-date="publishDate"
        :resetting="resetting"
        :discs="discs"
        :un-released-pair-providers="unReleasedPairProviders"
        :blank-schedule-providers="blankScheduleProviders"
        :confirm-product-restriction-errors="restrictionErrors"
        @onEditClicked="onEditClicked"
        @confirmed="onConfirmed">
      </release-step3-confirm>
      <div v-else>
        <form>
          <div class="form-wrap">
            <div
              class="form-group"
              :class="{
                'form-group-disabled': resetting
              }">
              <div class="form-label">
                <label
                  class="control-label">配信エリア</label>
              </div>
              <div class="form-item-inline">
                <span class="radio">
                  <label for="for_worldwide">
                    <input
                      id="for_worldwide"
                      v-model="country"
                      name="country"
                      type="radio"
                      value="WW"
                      :disabled="resetting"
                      checked>
                    <div class="option-input"></div> 世界(日本を含む)
                  </label>
                </span>
                <span class="radio">
                  <label for="for_domestic">
                    <input
                      id="for_domestic"
                      v-model="country"
                      name="country"
                      type="radio"
                      :disabled="resetting"
                      value="JP">
                    <div class="option-input"></div> 国内
                  </label>
                </span>
                <span
                  v-if="isBtoB"
                  class="radio">
                  <label for="for_outside">
                    <input
                      id="for_outside"
                      v-model="country"
                      name="country"
                      type="radio"
                      :disabled="resetting"
                      value="WW|-JP">
                    <div class="option-input"></div> 日本以外の世界
                  </label>
                </span>
              </div>
            </div>
            <div
              v-if="!resetting"
              class="form-group">
              <div class="form-label">
                <label
                  class="control-label">配信希望日
                  <i
                    v-tooltip="spTooltipConfig('配信希望日は、配信ストア側の審査状況によりご指定頂いた日付より遅れる場合がございます。')"
                    class="sp-tooltip-icon">
                  </i>
                  <i
                    v-tooltip="pcTooltipConfig('配信希望日は、配信ストア側の審査状況によりご指定頂いた日付より遅れる場合がございます。')"
                    class="tooltip-icon pc">
                  </i>
                </label>
              </div>
              <div class="form-item">
                <div class="form-item-inline simultaneous-release">
                  <div class="radio mr-30">
                    <label for="simultaneousReleaseFalse">
                      <input
                        id="simultaneousReleaseFalse"
                        v-model="simultaneousRelease"
                        name="simultaneousRelease"
                        type="radio"
                        :value="false"
                        checked>
                      <div class="option-input"></div>
                      ストア別の最短配信日で指定する
                    </label>
                    <transition name="slide">
                      <div
                        v-if="!simultaneousRelease&&isBtoC"
                        class="form-item-text mt-5 mb-10">
                        ※「ストア別の最短配信日で指定する」をご選択された場合、<a
                          href="/service/submit"
                          class="c-text-link"
                          target="_blank">
                          サブミット機能
                        </a>をご利用いただけません。
                      </div>
                    </transition>
                  </div>
                  <div class="radio">
                    <label for="simultaneousReleaseTrue">
                      <input
                        id="simultaneousReleaseTrue"
                        v-model="simultaneousRelease"
                        name="simultaneousRelease"
                        type="radio"
                        :value="true">
                      <div class="option-input"></div>
                      全ストア同日配信の最短日で指定する
                    </label>
                    <transition name="slide">
                      <div
                        v-if="simultaneousRelease"
                        class="simultaneous-release__select-box">
                        <date-select
                          v-if="isBtoB"
                          v-model="publishDate"
                          :min-date="minDate|formatDate('YYYY-MM-DD')"
                          :max-date="maxReleaseDate|formatDate('YYYY-MM-DD')"
                          suffix="以降" />
                        <date-picker
                          v-if="isBtoC"
                          v-model="publishDate"
                          :disabled-date="disableDatesForSimultaneousRelease"
                          :clearable="false"
                          :editable="false"
                          value-type="format"
                          @open="setDateRange()"
                          @calendar-change="onCalenderChange()">
                          <template #footer>
                            <submit-date-picker-footer />
                          </template>
                        </date-picker>
                        <div class="form-item-text mt-5 mb-10">
                          {{ minDate | formatDate('YYYY年MM月DD日') }}
                          以降の設定となります。
                        </div>
                        <template
                          v-if="restrictionErrors['publishDate']">
                          <div
                            v-for="(error,index) in restrictionErrors['publishDate']"
                            :key="`publishDateError${index}`"
                            class="price-error">
                            {{ error }}
                          </div>
                        </template>
                      </div>
                    </transition>
                  </div>
                </div>
              </div>
            </div>
            <div
              v-show="!contentsIdOnly"
              class="form-group-wrap__title pcmt20">
              配信先ストア
              <span class="form-item-text--inline">
                <router-link
                  :to="{ name:'store-detail' }"
                  class="c-text-link"
                  target="_blank">
                  配信先ストア詳細はこちら
                </router-link>
              </span>
            </div>
            <div
              v-show="!contentsIdOnly"
              class="form-group-wrap">
              <div class="check-list-heading">
                <span class="check-list-heading__title">サブスクリプション配信</span>
                <span class="check-list-heading__wrap">
                  <div class="checkbox check-list-heading__checkbox">
                    <label @click.prevent="checkAll(true)">
                      <input
                        type="checkbox"
                        disabled
                        checked>
                      <div class="option-input"></div>
                      全て選択
                    </label>
                  </div>
                  <div class="checkbox--cancel check-list-heading__checkbox">
                    <label @click.prevent="unCheckAll(true)">
                      <input
                        type="checkbox"
                        disabled
                        checked>
                      <div class="option-input"></div>全て解除
                    </label>
                  </div>
                </span>
              </div>
              <ul class="check-list">
                <li
                  v-for="provider in subscriptionProviders"
                  :key="`subscription-${provider.key}`"
                  class="check-list__node">
                  <div
                    class="checkbox"
                    :class="{
                      'form-group-disabled': (resetting&&releasedProviders[provider.key])
                        ||(youtubeForceCheck && provider.key === 'youtube')
                        ||isDisabledWhenChooseSpokenWord(provider.key)
                        ||isDisabledWhenChooseClassical(provider.key)
                        ||isDisabledWhenChooseVariousArtist(provider.key)
                        ||isDisabledWhenChoosePublicDomain(provider.key)
                        ||isDisabledWhenHasLongTrack(provider.key)
                        ||isDisabledWhenPositiveAnswers(provider.key)
                    }">
                    <label :class="{'form-group-disabled': grayOutProviders(provider.key)}">
                      <input
                        v-model="selectedProviders[provider.key]"
                        type="checkbox"
                        :disabled="(resetting&&releasedProviders[provider.key])
                          ||isDisabledWhenChooseSpokenWord(provider.key)
                          ||isDisabledWhenChooseClassical(provider.key)
                          ||isDisabledWhenChooseVariousArtist(provider.key)
                          ||isDisabledWhenChoosePublicDomain(provider.key)
                          ||isDisabledWhenHasLongTrack(provider.key)
                          ||isDisabledWhenPositiveAnswers(provider.key)"
                        @change="onChangeProviders">
                      <div class="option-input"></div>
                      {{ provider.display_name }}
                    </label>
                  </div>
                  <div
                    v-if="resetting&&releasedProviders[provider.key]"
                    class="min-release-select">
                    <div
                      style="flex-basis: 100%;"
                      class="min-release-description">
                      リリース済み
                      <span v-if="!blankScheduleProviders[provider.key]">
                        : {{ providerSchedules[provider.key]|formatDate('YYYY年MM月DD日') }}
                      </span>
                    </div>
                  </div>
                  <div v-else-if="grayOutProviders(provider.key)">
                    <div
                      style="flex-basis: 100%;"
                      class="min-release-description">
                      <span style="font-size: 12px; color: red;">
                        このストアへの配信は一時的に停止しています
                      </span>
                    </div>
                  </div>
                  <div v-else-if="isDisabledWhenChooseSpokenWord(provider.key)">
                    <div
                      style="flex-basis: 100%;"
                      class="min-release-description">
                      <span style="font-size: 12px; color: red;">
                        ジャンルが「Spoken Word」の場合は、
                        {{ provider.display_name }}
                        に配信できません。
                      </span>
                    </div>
                  </div>
                  <div v-else-if="isDisabledWhenChooseClassical(provider.key)">
                    <div
                      style="flex-basis: 100%;"
                      class="min-release-description">
                      <span style="font-size: 12px; color: red;">
                        ジャンルがclassicの場合は、SoundCloudに配信できません。
                      </span>
                    </div>
                  </div>
                  <div v-else-if="isDisabledWhenChooseVariousArtist(provider.key)">
                    <div
                      style="flex-basis: 100%;"
                      class="min-release-description">
                      <span style="font-size: 12px; color: red;">
                        アーティストが「Various Artist」の場合、SoundCloudに配信できません。
                      </span>
                    </div>
                  </div>
                  <div v-else-if="isDisabledWhenChoosePublicDomain(provider.key)">
                    <div
                      style="flex-basis: 100%;"
                      class="min-release-description">
                      <span style="font-size: 12px; color: red;">
                        楽曲区分が「パブリックドメイン」の場合、{{ provider.display_name }}に配信できません。
                      </span>
                    </div>
                  </div>
                  <div v-else-if="isDisabledWhenHasLongTrack(provider.key)">
                    <div
                      style="flex-basis: 100%;"
                      class="min-release-description">
                      <span style="font-size: 12px; color: red;">
                        10分以上の楽曲を含む場合、SoundCloudに配信できません。
                      </span>
                    </div>
                  </div>
                  <div v-if="isDisabledWhenPositiveAnswers(provider.key)">
                    <div
                      style="flex-basis: 100%;"
                      class="min-release-description">
                      <span
                        v-if="isBtoB"
                        style="font-size: 12px; color: red;">
                        フリートラックを使用している場合は
                        {{ provider.key === "musicstamp" ?
                          "Facebook/Instagram ミュージックスタンプ" : "SoundCloud" }}
                        に配信できません。
                      </span>
                      <span
                        v-else-if="isBtoC"
                        style="font-size: 12px; color: red;">
                        フリートラックや著作権フリー楽曲を使用している場合、または著作権フリー楽曲として販売していた実績がある場合、
                        {{ provider.key === "musicstamp" ?
                          "Facebook/Instagram ミュージックスタンプ" : "SoundCloud" }}
                        には配信できません。
                      </span>
                    </div>
                  </div>
                  <transition
                    v-else
                    name="slide">
                    <div
                      v-if="!simultaneousRelease
                        && !unReleasedPairProviders[provider.key]"
                      class="min-release-select">
                      <div
                        v-if="!hasProviderSchedules(provider.key)"
                        style="flex-basis: 100%;"
                        class="min-release-description">
                        最短リリース日:
                        {{ providerMinSchedules[provider.key]|formatDate('YYYY年MM月DD日') }}
                      </div>
                      <date-select
                        v-if="isBtoB && !hasProviderSchedules(provider.key)"
                        v-model="providerSchedules[provider.key]"
                        :min-date="providerMinSchedules[provider.key]"
                        :max-date="maxReleaseDate"
                        class="date-select"
                        @input="validate" />
                      <date-picker
                        v-if="isBtoC && !hasProviderSchedules(provider.key)"
                        v-model="providerSchedules[provider.key]"
                        :disabled-date="date => disableDatesForNonSimultaneousRelease(
                          date,
                          providerMinSchedules[provider.key])"
                        :clearable="false"
                        :editable="false"
                        value-type="format"
                        @input="validate">
                      </date-picker>
                      <a
                        v-if="false"
                        class="c-btn"
                        :class="{ 'c-btn-confirm': hasProviderSchedules(provider.key) }"
                        @click.prevent="onReleaseDateClicked(provider, provider.key)">
                        <span
                          v-if="hasProviderSchedules(provider.key)">
                          曲ごとの設定を確認
                        </span>
                        <span v-else>
                          曲ごとに設定
                        </span>
                        <span class="c-btn__icon-arrow"></span>
                      </a>
                      <template
                        v-if="restrictionErrors[provider.key]">
                        <div
                          v-for="(error,index) in restrictionErrors[provider.key]"
                          :key="`publishDateError${index}`"
                          class="price-error"
                          style="flex-basis: 100%;">
                          {{ error.replace(`${provider.display_name}の`,'') }}
                        </div>
                      </template>
                    </div>
                    <div
                      v-if="selectedProviders[provider.key]
                        && unReleasedPairProviders[provider.key]">
                      <p
                        class="min-release-description"
                        style="font-size: 13px;">
                        配信希望日の設定はできません。<br>
                        {{ getPairProviderName(provider.key) }} の配信開始日もしくは審査完了後すぐにて配信開始となります。
                      </p>
                    </div>
                  </transition>
                </li>
              </ul>
            </div>
            <div
              v-show="!contentsIdOnly"
              class="form-group">
              <div class="form-label">
                <label
                  class="control-label">ダウンロード価格
                  <i
                    v-tooltip="spTooltipConfig(message)"
                    class="sp-tooltip-icon">
                  </i>
                  <i
                    v-tooltip="pcTooltipConfig(message)"
                    class="tooltip-icon pc">
                  </i>
                </label>
              </div>
              <div class="form-item">
                <div class="form-item-inline form-item-inline--sp-reset">
                  <span class="form-item__wrap">
                    <div class="form-item-text">単曲のダウンロード価格</div>
                    <div class="c-select_wrap">
                      <select
                        v-model="singlePrice"
                        type="number"
                        class="form-control c-select c-select-value"
                        :disabled="hasReleasedDlProviders"
                        @change.prevent="onChangeSinglePrice">
                        <option value="">選択して下さい</option>
                        <option
                          v-for="price in singlePrices"
                          :key="price"
                          :value="price">
                          {{ price }}円
                        </option>
                      </select>
                    </div>
                  </span>
                  <span
                    v-if="showPackagePrice"
                    class="form-item__wrap">
                    <div class="form-item-text">パッケージのダウンロード価格</div>
                    <div class="c-select_wrap">
                      <select
                        v-model="packagePrice"
                        type="number"
                        class="form-control c-select c-select-value"
                        :disabled="hasReleasedDlProviders"
                        @change="onChangePackagePrice">
                        <option value="">選択して下さい</option>
                        <option
                          v-for="price in availablePackagePrices"
                          :key="price"
                          :value="price">
                          {{ price }}円
                        </option>
                      </select>
                    </div>
                  </span>
                </div>
                <p class="error-text">〇〇が入力されていません</p>
              </div>
            </div>
            <div
              v-show="!contentsIdOnly && isHiRes"
              class="form-group">
              <div class="form-label">
                <label
                  class="control-label">ダウンロード価格（ハイレゾ版）
                  <i
                    v-tooltip="spTooltipConfig(message)"
                    class="sp-tooltip-icon">
                  </i>
                  <i
                    v-tooltip="pcTooltipConfig(message)"
                    class="tooltip-icon pc">
                  </i>
                </label>
              </div>
              <div class="form-item">
                <div class="form-item-inline form-item-inline--sp-reset">
                  <span class="form-item__wrap">
                    <div class="form-item-text">単曲のダウンロード価格</div>
                    <div class="price-number-wrap">
                      <input
                        v-model="hiResSinglePrice"
                        type="number"
                        placeholder="150~550"
                        class="form-control"
                        onkeydown="return event.keyCode !== 13;"
                        :class="{ 'price-input-form-disabled': hasReleasedDlProviders }"
                        :disabled="hasReleasedDlProviders"
                        @change.prevent="onChangeSinglePrice">
                    </div>
                  </span>
                  <span
                    v-if="showPackagePrice"
                    class="form-item__wrap">
                    <div class="form-item-text">パッケージのダウンロード価格</div>
                    <div class="price-number-wrap">
                      <input
                        v-model="hiResPackagePrice"
                        type="number"
                        placeholder="300~5093"
                        class="form-control"
                        onkeydown="return event.keyCode !== 13;"
                        :class="{ 'price-input-form-disabled': hasReleasedDlProviders }"
                        :disabled="hasReleasedDlProviders"
                        @change.prevent="onChangePackagePrice">
                    </div>
                  </span>
                </div>
                <div
                  v-if="!readonly && priceErrors.length > 0"
                  class="price-error">
                  <div
                    v-for="(priceError, index) in priceErrors"
                    :key="index">
                    {{ priceError }}
                  </div>
                </div>
              </div>
            </div>
            <div
              v-show="!contentsIdOnly"
              class="form-group-wrap"
              :class="{ 'form-group-disabled': !downloadEnabled }">
              <div class="check-list-heading">
                <span class="check-list-heading__title">ダウンロード配信</span>
                <span class="check-list-heading__wrap">
                  <div class="checkbox check-list-heading__checkbox">
                    <label @click.prevent="checkAll(false)">
                      <input
                        type="checkbox"
                        disabled
                        checked>
                      <div class="option-input"></div>
                      全て選択
                    </label>
                  </div>
                  <div class="checkbox--cancel check-list-heading__checkbox">
                    <label @click.prevent="unCheckAll(false)">
                      <input
                        type="checkbox"
                        disabled
                        checked>
                      <div class="option-input"></div>全て解除
                    </label>
                  </div>
                </span>
              </div>
              <ul class="check-list">
                <li
                  v-for="provider in downloadProviders"
                  :key="`download-${provider.key}`"
                  class="check-list__node">
                  <div
                    class="checkbox"
                    :class="{
                      'form-group-disabled': !downloadEnabled
                        ||(resetting&&releasedProviders[provider.key])
                        ||isDisabledWhenChooseSpokenWord(provider.key)
                    }">
                    <label :class="{'form-group-disabled': grayOutProviders(provider.key)}">
                      <input
                        v-model="selectedProviders[provider.key]"
                        type="checkbox"
                        :disabled="!downloadEnabled
                          ||(resetting&&releasedProviders[provider.key])
                          ||isDisabledWhenChooseSpokenWord(provider.key)"
                        @change="onChangeProviders">
                      <div class="option-input"></div>
                      <template v-if="provider.key==='tencent-dl'">
                        <div>
                          {{ provider.display_name }}
                          <i
                            v-tooltip="spTooltipConfig(`1曲のダウンロード販売価格は一律2元 (CNY)です。
                            このストアでダウンロード販売を行う場合、販売価格は自動で設定されます。
                            あらかじめご了承ください。<br>
                            ※ダウンロード販売価格は今後変更される場合がございます`)"
                            class="sp-tooltip-icon"
                            onclick="return false;">
                          </i>
                          <i
                            v-tooltip="pcTooltipConfig(`1曲のダウンロード販売価格は一律2元 (CNY)です。
                            このストアでダウンロード販売を行う場合、販売価格は自動で設定されます。あらかじめご了承ください。<br>
                            ※ダウンロード販売価格は今後変更される場合がございます`)"
                            class="tooltip-icon pc"
                            style="display:inline;">
                          </i>
                        </div>
                      </template>
                      <template v-else>
                        {{ provider.display_name }}
                      </template>
                    </label>
                  </div>
                  <div
                    v-if="resetting&&releasedProviders[provider.key]"
                    class="min-release-select">
                    <div
                      style="flex-basis: 100%;"
                      class="min-release-description">
                      リリース済み
                      <span v-if="!blankScheduleProviders[provider.key]">
                        : {{ providerSchedules[provider.key]|formatDate('YYYY年MM月DD日') }}
                      </span>
                    </div>
                  </div>
                  <div v-else-if="grayOutProviders(provider.key)">
                    <div
                      style="flex-basis: 100%;"
                      class="min-release-description">
                      <span style="font-size: 12px; color: red;">
                        このストアへの配信は一時的に停止しています
                      </span>
                    </div>
                  </div>
                  <div v-else-if="isDisabledWhenChooseSpokenWord(provider.key)">
                    <div
                      style="flex-basis: 100%;"
                      class="min-release-description">
                      <span style="font-size: 12px; color: red;">
                        ジャンルが「Spoken Word」の場合は、 {{ provider.display_name }}に配信できません。
                      </span>
                    </div>
                  </div>
                  <transition
                    v-else
                    name="slide">
                    <div
                      v-if="!simultaneousRelease
                        && !unReleasedPairProviders[provider.key]"
                      class="min-release-select">
                      <div
                        style="flex-basis: 100%;"
                        class="min-release-description">
                        最短リリース日:
                        {{ providerMinSchedules[provider.key]|formatDate('YYYY年MM月DD日') }}
                      </div>
                      <date-select
                        v-if="isBtoB && !hasProviderSchedules(provider.key)"
                        v-model="providerSchedules[provider.key]"
                        :min-date="providerMinSchedules[provider.key]"
                        :max-date="maxReleaseDate"
                        class="date-select"
                        @input="validate" />
                      <date-picker
                        v-if="isBtoC && !hasProviderSchedules(provider.key)"
                        v-model="providerSchedules[provider.key]"
                        :disabled-date="date => disableDatesForNonSimultaneousRelease(
                          date, providerMinSchedules[provider.key])"
                        :clearable="false"
                        :editable="false"
                        value-type="format"
                        @input="validate">
                      </date-picker>
                      <a
                        v-if="false"
                        class="c-btn"
                        :class="{ 'c-btn-confirm': hasProviderSchedules(provider.key) }"
                        @click.prevent="onReleaseDateClicked(provider, provider.key)">
                        <span
                          v-if="hasProviderSchedules(provider.key)">
                          曲ごとの設定を確認
                        </span>
                        <span v-else>
                          曲ごとに設定
                        </span>
                        <span class="c-btn__icon-arrow"></span>
                      </a>
                      <template
                        v-if="restrictionErrors[provider.key]">
                        <div
                          v-for="(error,index) in restrictionErrors[provider.key]"
                          :key="`publishDateError${index}`"
                          class="price-error"
                          style="flex-basis: 100%;">
                          {{ error.replace(`${provider.display_name}の`,'') }}
                        </div>
                      </template>
                    </div>
                    <div
                      v-if="selectedProviders[provider.key]
                        && unReleasedPairProviders[provider.key]">
                      <p
                        class="min-release-description"
                        style="font-size: 13px;">
                        配信希望日の設定はできません。<br>
                        {{ getPairProviderName(provider.key) }} の配信開始日もしくは審査完了後すぐにて配信開始となります。
                      </p>
                    </div>
                  </transition>
                </li>
              </ul>
            </div>
            <div
              v-show="!contentsIdOnly"
              v-if="databaseProviders.length > 0"
              class="form-group-wrap">
              <div class="check-list-heading">
                <span class="check-list-heading__title">その他</span>
                <span class="check-list-heading__wrap">
                  <div class="checkbox check-list-heading__checkbox">
                    <label @click.prevent="checkDatabaseAll(true)">
                      <input
                        type="checkbox"
                        disabled
                        checked>
                      <div class="option-input"></div>
                      全て選択
                    </label>
                  </div>
                  <div class="checkbox--cancel check-list-heading__checkbox">
                    <label @click.prevent="unCheckDatabaseAll(true)">
                      <input
                        type="checkbox"
                        disabled
                        checked>
                      <div class="option-input"></div>全て解除
                    </label>
                  </div>
                </span>
              </div>
              <ul class="check-list">
                <li
                  v-for="provider in databaseProviders"
                  :key="`database-${provider.key}`"
                  class="check-list__node">
                  <div
                    class="checkbox"
                    :class="{
                      'form-group-disabled':
                        (resetting&&releasedProviders[provider.key])
                        ||(isNotMatchFacebookPolicy&&provider.key==='musicstamp')
                        ||isDisabledWhenChooseSpokenWord(provider.key)
                        ||isDisabledWhenPositiveAnswers(provider.key)
                    }">
                    <label>
                      <input
                        v-model="selectedProviders[provider.key]"
                        type="checkbox"
                        :disabled="
                          (resetting&&releasedProviders[provider.key])
                            ||(isNotMatchFacebookPolicy&&provider.key==='musicstamp')
                            ||isDisabledWhenChooseSpokenWord(provider.key)
                            ||isDisabledWhenPositiveAnswers(provider.key)
                        "
                        @change="onChangeProviders">
                      <div class="option-input"></div>
                      {{ provider.display_name }}
                    </label>
                  </div>
                  <div
                    v-if="resetting&&releasedProviders[provider.key]"
                    class="min-release-select">
                    <div
                      style="flex-basis: 100%;"
                      class="min-release-description">
                      リリース済み
                      <span v-if="!blankScheduleProviders[provider.key]">
                        : {{ providerSchedules[provider.key]|formatDate('YYYY年MM月DD日') }}
                      </span>
                    </div>
                  </div>
                  <div v-else-if="isNotMatchFacebookPolicy&&provider.key==='musicstamp'">
                    <div
                      style="flex-basis: 100%;"
                      class="min-release-description">
                      <span style="font-size: 12px; color: red;">
                        楽曲区分が「パブリックドメイン」「カバー」の場合、もしくはジャンルが「カラオケ」「Spoken Word」「サウンドトラック」の場合、
                        また10分以上の楽曲（その楽曲を含むアルバム）はFacebook/Instagram ミュージックスタンプに配信できません。
                      </span>
                    </div>
                  </div>
                  <div v-else-if="isDisabledWhenChooseSpokenWord(provider.key)">
                    <div
                      style="flex-basis: 100%;"
                      class="min-release-description">
                      <span style="font-size: 12px; color: red;">
                        ジャンルが「Spoken Word」の場合は、 {{ provider.display_name }}に配信できません。
                      </span>
                    </div>
                  </div>
                  <div v-if="isDisabledWhenPositiveAnswers(provider.key)">
                    <div
                      style="flex-basis: 100%;"
                      class="min-release-description">
                      <span
                        v-if="isBtoB"
                        style="font-size: 12px; color: red;">
                        フリートラックを使用している場合は
                        {{ provider.key === "musicstamp" ?
                          "Facebook/Instagram ミュージックスタンプ" : "SoundCloud" }}
                        に配信できません。
                      </span>
                      <span
                        v-else-if="isBtoC"
                        style="font-size: 12px; color: red;">
                        フリートラックや著作権フリー楽曲を使用している場合、または著作権フリー楽曲として販売していた実績がある場合、
                        {{ provider.key === "musicstamp" ?
                          "Facebook/Instagram ミュージックスタンプ" : "SoundCloud" }}
                        には配信できません。
                      </span>
                    </div>
                  </div>
                  <transition
                    v-else
                    name="slide">
                    <div
                      v-if="!simultaneousRelease"
                      class="min-release-select">
                      <div
                        v-if="!hasProviderSchedules(provider.key)"
                        style="flex-basis: 100%;"
                        class="min-release-description">
                        最短リリース日:
                        {{ providerMinSchedules[provider.key]|formatDate('YYYY年MM月DD日') }}
                      </div>
                      <date-select
                        v-if="isBtoB && !hasProviderSchedules(provider.key)"
                        v-model="providerSchedules[provider.key]"
                        :min-date="providerMinSchedules[provider.key]"
                        :max-date="maxReleaseDate"
                        class="date-select"
                        @input="validate" />
                      <date-picker
                        v-if="isBtoC && !hasProviderSchedules(provider.key)"
                        v-model="providerSchedules[provider.key]"
                        :disabled-date="date => disableDatesForNonSimultaneousRelease(
                          date, providerMinSchedules[provider.key])"
                        :clearable="false"
                        :editable="false"
                        value-type="format"
                        @input="validate">
                      </date-picker>
                      <a
                        v-if="false"
                        class="c-btn"
                        :class="{ 'c-btn-confirm': hasProviderSchedules(provider.key) }"
                        @click.prevent="onReleaseDateClicked(provider, provider.key)">
                        <span
                          v-if="hasProviderSchedules(provider.key)">
                          曲ごとの設定を確認
                        </span>
                        <span v-else>
                          曲ごとに設定
                        </span>
                        <span class="c-btn__icon-arrow"></span>
                      </a>
                      <template
                        v-if="restrictionErrors[provider.key]">
                        <div
                          v-for="(error,index) in restrictionErrors[provider.key]"
                          :key="`publishDateError${index}`"
                          class="price-error"
                          style="flex-basis: 100%;">
                          {{ error.replace(`${provider.display_name}の`,'') }}
                        </div>
                      </template>
                    </div>
                  </transition>
                </li>
              </ul>
            </div>
          </div>
        </form>
        <div class="release-caution-area release-caution-area--store">
          <div class="release-caution-area__title">【ご注意】</div>
          <div class="release-caution-area__paragraph">
            <p class="u-bold">■サブスクリプション配信</p>
            <p>
              ・一部の国において、販売収益は異なります。今後配信国が追加された場合は、自動的に配信が開始されます。
            </p>
            <p>
              ・SoundCloudに配信した場合、現状、日本では有料プラン(SoundCloud Goなど)はローンチされておらず、
              無料かつ広告なし・オンデマンドで 楽曲を全再生できる状況です。
              また日本での配信においては、再生によるロイヤリティは発生しません。
            </p>
            <p>
              ・当サービスから配信している楽曲を既にSoundCloudの自分のチャンネルで配信している場合、
              「著作権侵害の申し立て」と判断されブロックされる可能性があります。
            </p>
            <p>
              <template v-if="isBtoB">
                ・楽曲区分が「パブリックドメイン」の場合、アーティストが「Various Artist」の場合、
                もしくはジャンルが「クラシック」の場合、また10分以上の楽曲（その楽曲を含むアルバム）はSoundCloudに配信できません。
              </template>
              <template v-else>
                ・楽曲区分が「パブリックドメイン」の場合、もしくはアーティストが「Various Artist」の場合、SoundCloudに配信できません。
              </template>
            </p>
          </div>
          <div class="release-caution-area__paragraph">
            <p class="u-bold">■ダウンロード配信</p>
            <p>
              ・今後、選択された配信地域に新たな国が追加された場合は、自動的に配信が開始されます。
              iTunes Storeを選択された場合、自動的に「iTunes Match」及び「iTunes Radio」へも配信されます。
              海外の配信事業者から海外地域を対象に配信する際には、当該配信事業者が指定する通貨に基づき販売価格が設定されます。
            </p>
            <p>
              ・日本円での表示は参考価格です。
            </p>
          </div>
          <div class="release-caution-area__paragraph">
            <p class="u-bold">■その他</p>
            <p>
              ・カバー楽曲を販売（配信）する場合は、翻案権等や著作者人格権（同一性保持権）等に抵触する可能性がありますので、音楽出版者または著作者へ必ずご確認ください。
            </p>
            <p>
              ・Shazamのデータベースへ登録した場合でも、Shazam上では楽曲の再生がないため、ロイヤリティは発生しません。
            </p>
            <p>
              ・楽曲区分が「パブリックドメイン」「カバー」の場合、もしくはジャンルが「カラオケ」「Spoken Word」「サウンドトラック」の場合は、
              Facebook/Instagram ミュージックスタンプに配信することはできません。
            </p>
            <p>
              ・7digitalへ配信する場合、 7digitalが提携するストアにも配信される場合がございます。
            </p>
            <p>
              ・Melon / Bugs! / Naver VIBE / Genieは独自の審査基準により、楽曲が90秒未満の場合は配信ができない可能性がございます。<br>
              また、ストアの基準により配信不可、または希望の配信日より遅れる場合があります。
            </p>
            <p v-if="isBtoB">
              ・Bugs! / Naver VIBE / Genieについて、ご希望の「配信開始日」がある場合、
              「配信開始日」の3週間前までにリリース申請を行なって頂くことをお勧めします。<br>
            </p>
          </div>
        </div>
      </div>
      <div
        v-if="!readonly && storeErrors.length > 0"
        class="store-error">
        <div
          v-for="(storeError, index) in storeErrors"
          :key="index">
          {{ storeError }}
        </div>
      </div>
      <div
        v-if="(confirmed && confirmErrors.length > 0) || isErrorFromSecurityCodeScreen"
        class="store-error">
        <div
          v-for="(errors, index) in confirmErrors"
          :key="index">
          <div
            v-for="(error, eIndex) in errors"
            :key="`cerror-${eIndex}`">
            {{ error }}
          </div>
        </div>
      </div>
      <div v-if="!readonly">
        <div
          v-if="showConfirmation"
          class="c-btn-wrap">
          <a
            v-if="isBtoC && (status === 0||status === 2) && checkIfUserDataAvailable()"
            class="c-btn"
            :class="{ 'c-btn--off' : !confirmed }"
            @click.prevent="showSection('confirm-user-data')">
            アカウント情報の確認へ
            <span class="c-btn__icon-arrow"></span></a>
          <a
            v-else-if="isBtoC && (status === 0||status === 2) && !checkIfUserDataAvailable()"
            class="c-btn"
            :class="{ 'c-btn--off' : !confirmed }"
            :disabled="!confirmed"
            @click.prevent="showSection('user-data-input')">
            アカウント情報の登録へ
            <span class="c-btn__icon-arrow"></span></a>
          <a
            v-else-if="isBtoB && (status === 0||status === 2)"
            class="c-btn"
            :class="{ 'c-btn--off' : !confirmed }"
            :disabled="!confirmed"
            href="#"
            @click.prevent="onReleaseClicked">
            {{ isSubscriber || isBtoB || hasSettled === 1 || isMate ? '配信審査を依頼する' : '決済にすすむ' }}
            <span class="c-btn__icon-arrow"></span></a>
          <a
            v-if="showConfirmation"
            class="c-btn c-btn--back c-btn--back-left"
            href="#"
            @click.prevent="onReturn2Clicked">
            <span>戻る</span>
            <span class="c-btn__icon-arrow c-btn__icon-arrow--left"></span>
          </a>
          <a
            v-if="status !== 0"
            class="c-btn c-btn--back c-btn--back-left"
            href="#"
            @click.prevent="onEditClicked">
            <span>戻る</span>
            <span class="c-btn__icon-arrow c-btn__icon-arrow--left"></span>
          </a>
        </div>
        <div
          v-else
          class="c-btn-wrap">
          <a
            class="c-btn"
            :class="{ 'c-btn--off': nextButtonDisabled }"
            @click.prevent="onNextClicked">確認する
            <span class="c-btn__icon-arrow"></span>
          </a>
          <a
            id="backButtonRelease"
            class="c-btn c-btn--back c-btn--back-left"
            @click="onBackClicked">
            戻る
            <span class="c-btn__icon-arrow c-btn__icon-arrow--left"></span>
          </a>
        </div>
      </div>
      <div
        v-else
        class="c-btn-wrap">
        <a
          class="c-btn c-btn--back c-btn--back-left"
          href="#"
          @click="onReturnClicked">
          <span>戻る</span>
          <span class="c-btn__icon-arrow c-btn__icon-arrow--left"></span>
        </a>
        <a
          v-if="enableStoreReSetting"
          class="c-btn"
          href="#"
          @click.prevent="onStoreSettingClicked">
          <span>ストアを追加する</span>
          <span class="c-btn__icon-arrow"></span>
        </a>
      </div>
      <release-date-modal
        :provider-key="selectedProviderKey"
        :provider="releaseDateModalProvider"
        :min-date="providerMinSchedules[`${selectedProviderKey}`]"
        :max-date="maxReleaseDate"
        :discs="discs"
        :is-shown="releaseDateModalIsShown"
        @closed="onReleaseDateModalClosed"
        @cancel-clicked="releaseDateModalIsShown = false" />
    </div>
  </div>
  <release-user-info-confirm-and-sms-auth
    v-else
    :content="display"
    :mobile="mobileNumber"
    @show-section="showSection"
    @on-release-clicked="onUpdateRelease">
  </release-user-info-confirm-and-sms-auth>
</template>

<script>
import moment from 'moment-timezone'
import { mapGetters } from 'vuex'
import { cloneDeep, isEqual } from 'lodash'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import 'vue2-datepicker/locale/ja'
import ReleaseStep3Confirm from './Confirm'
import DateSelect from '../../DateSelect'
import ReleaseDateModal from './ReleaseDateModal'
import tooltip from '../../../mixins/tooltip'
import ReleaseUserInfoConfirmAndSmsAuth from '../Step4/Index'
import SubmitDatePickerFooter from './SubmitDatePickerFooter'

export default {
  name: 'ReleaseDistribution',
  components: {
    DateSelect,
    DatePicker,
    ReleaseDateModal,
    ReleaseStep3Confirm,
    ReleaseUserInfoConfirmAndSmsAuth,
    SubmitDatePickerFooter,
  },
  mixins: [
    tooltip,
  ],
  props: {
    singlePrices: {
      type: Array,
      required: false,
      default: () => [150, 200, 250],
    },
    packagePrices: {
      type: Array,
      required: false,
      default: () => [
        300, 450, 600, 750, 900, 1050, 1200, 1350, 1500, 1650, 1800, 2000, 2100, 2200, 2400,
      ],
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    resetting: {
      type: Boolean,
      default: false,
    },
    backFromRelease: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      simultaneousRelease: true, // 一括設定
      submitReleaseForm: false,
      initialSubmitReleaseValue: false,
      showSubmitForm: false,
      backFromReleaseTemp: false,
      initialInputValues: {},
      status: 1,
      country: 'WW',
      providers: [], // 配信先情報マスタ
      selectedProviders: {}, // 選択ストア状態
      providerSchedules: {}, // ストアごとの公開日
      providerMinSchedules: {}, // ストアごとの最短公開日
      publishDate: '',
      singlePrice: '',
      packagePrice: '',
      hiResSinglePrice: '',
      hiResPackagePrice: '',
      savedPackagePrice: '',
      savedHiResPackagePrice: '',
      showConfirmation: false,
      confirmed: false,
      confirmErrors: [],
      type: '',
      discs: [{ tracks: [] }],
      availablePackagePrices: [],
      minDate: null,
      storeErrors: [],
      priceErrors: [],
      restrictionErrors: {},
      maxReleaseDate: [],
      releaseDateModalIsShown: false,
      selectedProviderKey: '',
      releaseDateModalProvider: {},
      releasedProviders: {},
      hasReleasedDlProviders: false,
      youtubeForceCheck: false,
      contentsIdOnly: false,
      unReleasedPairProviders: {},
      blankScheduleProviders: {},
      confirmTrackErrors: {},
      isNotMatchFacebookPolicy: false,
      isHiRes: false,
      message: `<b>販売価格</b><br>
        [パッケージ価格]は選択された[単曲のダウンロード価格]の販売価格(税込) x パッケージの楽曲数で<br>
        算出された金額よりも低く設定する必要がございます。<br>
        例えば、10曲入りのパッケージで[単曲のダウンロード価格] 販売価格(税込)を￥200に選択された場合、<br>
        [パッケージ価格]の販売価格(税込)は ￥200 x 10曲の￥2,000が上限となります。<br>
        ※販売価格は選択された価格に近しい値が各ストア毎に設定されます。<br>
        ※販売価格はあくまでも参考値であり、配信ストア側によって変更される場合があります。`,
      isSpokenWord: false,
      submitDay: 28,
      disabledProvidersSpokenWord: [
        '7digital', '7digital-dl', 'amazon', 'amazon-dl', 'anghami', 'beatport', 'beatport-dl', 'deezer', 'gracenote',
        'jiosaavn', 'mixcloud', 'musicstamp', 'pandora', 'shazam', 'soundcloud', 'uma', 'uma-dl', 'yandex', 'youtube',
      ],
      isVariousArtist: false,
      isClassical: false,
      hasLongTrack: false,
      mobileNumber: '',
      display: 'release',
      trackCount: 0,
      isErrorFromSecurityCodeScreen: false,
      dateRange: [],
    }
  },
  computed: {
    downloadProviders() {
      return this.providers
        .filter(provider => ((this.country === 'WW')
            || (this.country === 'JP' && provider.for_domestic)
            || (this.country === 'WW|-JP' && provider.for_worldwide))
          && !provider.is_subscription
          && !provider.is_database_service
          && !provider.joint_provider_id)
        .sort((provider1, provider2) => (provider1.sort_index < provider2.sort_index ? -1 : 1))
    },
    subscriptionProviders() {
      const subscriptionProviderList = []
      this.providers.forEach(existedProvider => {
        // eslint-disable-next-line max-len
        if (!subscriptionProviderList.filter(subscriptionProvider => subscriptionProvider.display_name
          === existedProvider.display_name).length > 0) {
          subscriptionProviderList.push(existedProvider)
        }
      })
      return subscriptionProviderList
        .filter(provider => ((this.country === 'WW')
            || (this.country === 'JP' && provider.for_domestic)
            || (this.country === 'WW|-JP' && provider.for_worldwide))
          && provider.is_subscription
          && !provider.is_database_service
          && !provider.joint_provider_id)
        .sort((provider1, provider2) => (provider1.sort_index < provider2.sort_index ? -1 : 1))
    },
    jointProviderMap() {
      const providerMap = {}
      this.providers
        .forEach(provider => {
          if (provider.joint_provider_id) {
            const jointKey = this.providers
              .find(p => p.id === provider.joint_provider_id).key
            if (!providerMap[jointKey]) {
              providerMap[jointKey] = [provider.key]
            } else {
              providerMap[jointKey].push(provider.key)
            }
          }
        })
      return providerMap
    },
    databaseProviders() {
      return this.providers
        .filter(provider => provider.is_database_service)
        .sort((provider1, provider2) => (provider1.sort_index < provider2.sort_index ? -1 : 1))
    },
    // 選択可能な年配列
    years() {
      return [moment().year(), moment().year() + 1]
    },
    // 確認するボタンの無効状態
    nextButtonDisabled() {
      if (this.$store.state.auth.permission === 10) {
        return false
      }
      const selects =  Object.keys(this.selectedProviders)
        .filter(provider => this.selectedProviders[provider])

      return selects.length === 0
        || this.storeErrors.length > 0
        || this.priceErrors.length > 0
    },
    // ダウンロードストアへの配信向け
    downloadEnabled() {
      if (this.isHiRes) {
        return (this.hiResSinglePrice || this.hiResPackagePrice)
          && (this.singlePrice || this.packagePrice)
      }
      return this.singlePrice || this.packagePrice
    },
    // パッケージ価格欄を出すかどうか
    showPackagePrice() {
      return this.type === 'album' || this.discs[0].tracks.length > 1
    },
    enableStoreReSetting() {
      if (this.status !== 2 || this.releasedProviders.length === 0) {
        return false
      }
      const releasedKeys = Object.keys(this.releasedProviders)
      const selectedKeys = Object.keys(this.selectedProviders)

      const diff = selectedKeys.filter(key => releasedKeys.indexOf(key) === -1)

      return diff.length > 0
    },
    totalTracks() {
      let total = 0
      this.discs.forEach(disc => {
        total += disc.tracks.length
      })
      return total
    },
    ...mapGetters('auth', ['isSubscriber', 'isMate']),
  },
  watch: {
    nextButtonDisabled() {
      this.validate()
    },
    simultaneousRelease() {
      this.validate()
    },
    showConfirmation(value) {
      this.$emit('changeConfirm', value)
    },
    backFromRelease(value) {
      this.backFromReleaseTemp = value
    },
  },
  async mounted() {
    // retrieve current user data
    await this.getUser()
    // added for showing account confirmation screen if back from payment screen
    const isReturned = window.sessionStorage.getItem('back_from_payment_page')
    if (isReturned) {
      this.confirmed = false
      this.display = 'confirm-user-data'
      window.sessionStorage.removeItem('back_from_payment_page')
    }
    this.fetchItems().then(() => {
      if (this.youtubeForceCheck) {
        this.$set(this.selectedProviders, 'youtube', true)
        this.onChangeProviders()
      }
      if (this.isDisabledWhenPositiveAnswers('musicstamp') || this.isDisabledWhenPositiveAnswers('soundcloud')) {
        this.$set(this.selectedProviders, 'musicstamp', false)
        this.$set(this.selectedProviders, 'soundcloud', false)
        this.onChangeProviders()
      }
      this.initialInputValues = cloneDeep(this.$data)
    })
    // submit feature is only for BtoC
    if (this.isBtoC) {
      this.getSubmitDays()
      this.submitReleaseForm = window.sessionStorage.getItem('submitReleaseForm') ? JSON.parse(window.sessionStorage.getItem('submitReleaseForm')) : false
      this.initialSubmitReleaseValue = this.submitReleaseForm
      window.sessionStorage.setItem('submitReleaseForm', this.submitReleaseForm)
    }
    // セッションから値を復元
    const release = JSON.parse(window.sessionStorage.getItem('album'))
    if (!release) {
      this.$router.push({ name: 'index' })
      return
    }

    this.status = release.status
    // 納品中（テイクダウン）の時は申請出来ないようにする
    if (release.release_status === 14) {
      this.status = -1
    }
    // 下書き状態以外は確認画面だけを出す。
    if (this.status === 1) {
      this.showConfirmation = true
    }

    this.type = release.type
    this.discs = release.discs
    // contents_id_enabledのトラックがひとつでもあり、ジャンルに "Spoken Word"が含まれない
    if (this.checkTracksYoutubeState(this.discs, 'contents_id_enabled', 1)
        && !([release.product.genre1, release.product.genre2].some(genre => genre === 'Spoken Word'))) {
      this.youtubeForceCheck = true
      // すべてのトラックでcontents_id_enabledのみがONで、contents_enabledがOFF
      // const allContentsIdEnabled = !this.checkTracksYoutubeState(
      //   this.discs,
      //   'contents_id_enabled',
      //   0
      // )
      // const noContentsEnabled = !this.checkTracksYoutubeState(this.discs, 'contents_enabled', 1)
      // if (noContentsEnabled && allContentsIdEnabled) this.contentsIdOnly = true
    }
    // this.simultaneousRelease = release.store ? release.store.simultaneous_release : true
    if (this.resetting) {
      this.simultaneousRelease = false
    }
    this.publishDate = release.store ? release.store.publish_date : moment().format('YYYY-MM-DD')
    if (this.submitReleaseForm) this.publishDate = moment(new Date(this.publishDate)).add(this.submitDay, 'd').format('YYYY-MM-DD')
    else this.publishDate = moment(new Date(this.publishDate)).subtract(this.submitDay, 'd').format('YYYY-MM-DD')
    this.singlePrice = release.store.single_price
    this.packagePrice = release.store.package_price
    this.hiResSinglePrice = release.store.hi_res_single_price
    this.hiResPackagePrice = release.store.hi_res_package_price
    this.savedPackagePrice = this.packagePrice
    this.savedHiResPackagePrice = release.store.hi_res_package_price
    this.country = release.store ? release.store.country : 'WW'
    this.releasedProviders = release.releasedProviders
    this.isHiRes = release.discs.some(disk => disk.tracks.some(track => track.bit_length === '24'))
    this.hasSettled = release.has_settled ?? 0

    this.maxReleaseDate = moment().add(3, 'month').add(this.submitDay, 'd').format('YYYY-MM-DD')
    this.onChangeSinglePrice()
    this.onCheckUnReleasedPairProviders()
    this.onCheckBlankScheduleProviders()
    this.$emit('stateDetected', { status: this.status, type: this.type, contentsIdOnly: this.contentsIdOnly })
  },
  methods: {
    addDate(release, provider) {
      this.$set(
        this.providerSchedules,
        provider.key,
        release.providerSchedules && release.providerSchedules[provider.key]
          ? release.providerSchedules[provider.key]
          : provider.min_release_date,
      )
      this.$set(this.providerMinSchedules, provider.key, provider.min_release_date)
    },
    subtractDate(release, provider) {
      this.$set(
        this.providerSchedules,
        provider.key,
        release.providerSchedules && release.providerSchedules[provider.key]
          ? release.providerSchedules[provider.key]
          : provider.min_release_date,
      )
      this.$set(this.providerMinSchedules, provider.key, provider.min_release_date)
    },

    getReturnState() {
      // eslint-disable-next-line guard-for-in,no-restricted-syntax
      for (const key in this.$data) {
        const ignoreValues = ['initialInputValues', 'storeErrors', 'priceErrors', 'minDate', 'providers']
        if (!ignoreValues.includes(key)) {
          if (!isEqual(this.$data[key], this.initialInputValues[key])) {
            this.backFromReleaseTemp = true
            break // Exit the loop if any change is detected
          }
        }
      }
      this.$emit('back-from-release', {
        backFromRelease: this.backFromReleaseTemp,
      })
      return this.backFromReleaseTemp
    },
    onBackClicked() {
      if (!this.getReturnState()) {
        this.onBackConfirmed()
      }
    },
    onBackConfirmed() {
      this.$emit('is-confirmed', {
        isConfirmed: true,
      })
      this.backFromReleaseTemp = false
      this.$emit('back-from-release', {
        backFromRelease: this.backFromReleaseTemp,
      })
      if (this.resetting) {
        this.$router.push({ name: 'release-detail' })
      } else {
        this.$router.push({ name: `${this.$route.meta.sideMenuId}-step2` })
      }
    },
    // ロシア系サービスの対応
    grayOutProviders(key) {
      if (key === 'yandex' || key === 'uma' || key === 'uma-dl') {
        return true
      }
      return false
    },
    async getUser() {
      const { data } = await window.axios.get(`/api/client/users/${this.$store.state.auth.id}`)
      this.userData = data
      if (this.userData.mobile_number) {
        // Remove the prefix '+81' and add '0' to the beginning
        this.mobileNumber = `0${this.userData.mobile_number.slice(3)}`
      }
      /* eslint-disable camelcase */
      const {
        id,
        bank_code,
        bank_name,
        bank_branch_code,
        bank_branch_name,
        bank_account_type,
        bank_account_code,
        bank_account_name,
        prefecture,
        address1,
        address2,
        contact_name,
        postal_code,
      } = this.userData.company
      const { dob } = this.userData
      /* eslint-enable camelcase */
      window.sessionStorage.setItem('account_information', JSON.stringify({
        id,
        bank_code,
        bank_name,
        bank_branch_name,
        bank_branch_code,
        bank_account_type,
        bank_account_code,
        bank_account_name,
        prefecture,
        address1,
        address2,
        contact_name,
        dob,
        postal_code,
      }))
    },
    checkIfUserDataAvailable() {
      const requiredFields = [
        'address1',
        'bank_name',
        'bank_account_code',
        'bank_account_name',
        'bank_branch_name',
        'bank_account_type',
        'contact_name',
        'postal_code',
        'prefecture',
      ]

      return requiredFields.every(field => !!this.userData.company[field])
          && this.userData.dob !== null
    },
    async getSubmitDays() {
      const currentDate = new Date().toISOString().substr(0, 10)
      const { data } = await window.axios.get('/api/client/submitDate/getDay', {
        params: {
          currentDate,
        },
      })
      this.submitDay = data
    },
    // 配信ストア情報を取得
    async fetchItems() {
      // ストレージから選択済みストア情報を復元
      const release = JSON.parse(window.sessionStorage.getItem('album'))
      const { data }  = await window.axios.get('/api/client/providers')
      if (release.product.classification % 2 === 0
        || release.discs.some(disk => disk.tracks.some(track => track.classification % 2 === 0))
        || [release.product.genre1, release.product.genre2].some(
          genre => genre === 'Karaoke' || genre === 'Spoken Word' || genre === 'Soundtrack',
        )
      ) {
        release.providers.musicstamp = false
        this.isNotMatchFacebookPolicy = true
      }
      // ジャンルに'Spoken Word'が指定されていたらdisabledProvidersSpokenWordに定義されてるプロバイダーは選択を解除
      this.isSpokenWord = [release.product.genre1, release.product.genre2].some(genre => genre === 'Spoken Word')
      if (this.isSpokenWord) {
        for (let i = 0; i < this.disabledProvidersSpokenWord.length; i += 1) {
          const providerKey = this.disabledProvidersSpokenWord[i]
          release.providers[providerKey] = false
        }
      }
      // ジャンルにclassicalが設定されていたらsoundcloudを選択解除
      this.isClassical = [release.product.genre1, release.product.genre2].some(genre => genre === 'Classical')
      if (this.isClassical) {
        release.providers.soundcloud = false
      }
      // 10分以上の楽曲の場合にfb/insta,soundcloudの選択解除
      if (release.discs.some(d => d.tracks.some(t => !t.duration || parseInt(t.duration.split(':')[0], 10) > 0 || parseInt(t.duration.split(':')[1], 10) >= 10))) {
        this.hasLongTrack = true

        release.providers.musicstamp = false
        this.isNotMatchFacebookPolicy = true

        release.providers.soundcloud = false
      }

      // variousArtistのフラグ
      this.isVariousArtist = release.artistType === 2

      this.publishDate = release.store.publish_date ? moment(release.store.publish_date) : moment()
      const businessDirection = this.isBtoB ? 'b2b' : 'b2c'
      this.providers = this.status === 1
        ? data.filter(provider => release.providers[provider.key])
        : data.filter(provider => provider.business_direction === 'all' || provider.business_direction === businessDirection)
      if (![release.product.genre1, release.product.genre2].some(genre => genre === 'Dance' || genre === 'Electronica')) {
        // ジャンルに"ダンス"・"エレクトロニクス"が指定されているときだけ"beatport"を配信先に表示
        this.providers = this.providers.filter(provider => (!/^beatport(-dl)?$/.test(provider.key)))
      }
      this.providers.forEach(provider => {
        this.$set(this.selectedProviders, provider.key, release.providers[provider.key] || false)
        if (this.initialSubmitReleaseValue) {
          this.subtractDate(release, provider)
        } else {
          this.addDate(release, provider)
        }
      })

      if (this.status === 0) {
        // 最短日を計算
        this.onChangeProviders()
      }

      // 再設定の場合にダウンロード配信がなければ販売価格をクリアする
      if (this.resetting) {
        const releasedDlProviders = this.downloadProviders
          .filter(provider => this.releasedProviders[provider.key])
        this.hasReleasedDlProviders = releasedDlProviders.length > 0
        if (!this.hasReleasedDlProviders) {
          this.singlePrice = ''
          this.packagePrice = ''
        }
        this.validate()
      }
    },
    checkSubmitDateIsSelected() {
      this.submitReleaseForm = false
      if (this.simultaneousRelease) {
        // check user selected date is greater or equal to the submit possible date
        this.submitReleaseForm = this.publishDate >= this.getSubmitDate().format('YYYY-MM-DD')
      } else {
        const filteredSelectedProvider = Object.keys(this.selectedProviders)
          .filter(providerKey => this.selectedProviders[providerKey])
        // check user selected date is greater or equal to the submit possible date
        if (filteredSelectedProvider.some(
          providerKey => this.providerSchedules[providerKey]
            >= this.getSubmitDate()
              .format('YYYY-MM-DD'),
        )) {
          this.submitReleaseForm = true
        }
      }
    },
    // 確認するボタンクリック
    async onNextClicked() {
      // check submit date is selected in BtoC and if simultaneous release radio is selected.
      if (this.isBtoC && this.simultaneousRelease) {
        this.checkSubmitDateIsSelected()
        window.sessionStorage.setItem('submitReleaseForm', this.submitReleaseForm)
      }
      if (!this.simultaneousRelease) {
        window.sessionStorage.setItem('submitReleaseForm', false)
      }
      // check required user data are registered
      this.$emit('isUserDataComplete', this.checkIfUserDataAvailable())
      // 選択可能なストアのみ有効化
      Object.keys(this.selectedProviders).forEach(providerKey => {
        this.selectedProviders[providerKey] = (
          this.selectedProviders[providerKey] && (
            (`${this.singlePrice}${this.packagePrice}` !== ''
              && this.downloadProviders.filter(p => p.key === providerKey).length > 0)
            || this.subscriptionProviders.filter(p => p.key === providerKey).length > 0
            || this.databaseProviders.filter(p => p.key === providerKey).length > 0
          )
        )
      })
      const release = JSON.parse(window.sessionStorage.getItem('album'))
      try {
        // 配信日のバリデーションを行う
        this.restrictionErrors = {}
        const requestProviders = { ...this.selectedProviders }
        const requestSchedules = { ...this.providerSchedules }
        Object.keys(this.selectedProviders).forEach(providerKey => {
          if (this.selectedProviders[providerKey]
               && this.jointProviderMap[providerKey]) {
            this.jointProviderMap[providerKey].forEach(jointKey => {
              requestProviders[jointKey] = true
              requestSchedules[jointKey] = this.providerSchedules[providerKey]
            })
          }
        })
        await window.axios.post(`/api/client/products/${release.product.id}/restriction`, {
          providers: { ...requestProviders },
          schedules: { ...requestSchedules },
          publishDate: typeof this.publishDate === 'string' ? this.publishDate : this.publishDate.format('YYYY-MM-DD'),
          simultaneous_release: this.simultaneousRelease,
          validate_only: true,
        })

        // フォームをセッションに保存
        release.providers = { ...this.selectedProviders }
        release.providerSchedules = { ...this.providerSchedules }
        release.discs = this.discs
        release.store = {
          simultaneous_release: this.simultaneousRelease,
          publish_date: this.publishDate,
          single_price: this.singlePrice,
          package_price: this.packagePrice,
          hi_res_single_price: this.hiResSinglePrice,
          hi_res_package_price: this.hiResPackagePrice,
          country: this.country,
        }
        window.sessionStorage.setItem('album', JSON.stringify(release))
        // 確認画面を表示
        this.scrollToTop()
        this.savedPackagePrice = this.packagePrice
        this.savedHiResPackagePrice = this.hiResPackagePrice
        this.showConfirmation = true
      } catch ({ response }) {
        if (response.status === 422) {
          this.restrictionErrors = {}
          Object.keys(response.data.errors).forEach((key) => {
            this.restrictionErrors[key] = response.data.errors[key]
          })
          this.scrollToTop()
        }
      }
    },
    // 全て選択
    checkAll(isSubscription) {
      let providers = isSubscription
        ? this.subscriptionProviders
        : this.downloadProviders

      if (this.resetting) {
        providers = providers.filter(provider => !this.releasedProviders[provider.key])
      }
      providers.forEach(provider => {
        // ロシア系のストア対応
        if (provider.key === 'yandex' || provider.key === 'uma' || provider.key === 'uma-dl') {
          return
        }
        // ジャンルがSpoken Wordの時配信出来ないストアとポジティブな時にsoundcloudとmusicstampのは弾く
        if (this.isDisabledWhenChooseSpokenWord(provider.key)
            || this.isDisabledWhenChooseClassical(provider.key)
            || this.isDisabledWhenChoosePublicDomain(provider.key)
            || this.isDisabledWhenChooseVariousArtist(provider.key)
            || this.isDisabledWhenHasLongTrack(provider.key)
            || this.isDisabledWhenPositiveAnswers(provider.key)) {
          return
        }
        this.$set(this.selectedProviders, provider.key, true)
      })
      this.onChangeProviders()
    },
    // 全て解除
    unCheckAll(isSubscription) {
      let providers = isSubscription
        ? this.subscriptionProviders
        : this.downloadProviders
      if (this.resetting) {
        providers = providers.filter(provider => !this.releasedProviders[provider.key])
      }
      providers.forEach(provider => {
        this.$set(this.selectedProviders, provider.key, false)
      })
      if (this.youtubeForceCheck) this.$set(this.selectedProviders, 'youtube', true)
      this.onChangeProviders()
    },
    // 全て選択
    checkDatabaseAll() {
      let providers = this.databaseProviders

      if (this.resetting) {
        providers = providers.filter(provider => !this.releasedProviders[provider.key])
      }
      providers.forEach(provider => {
        // ジャンルがSpoken Wordの時配信出来ないストアとポジティブな時にsoundcloudとmusicstampのは弾く
        if (this.isDisabledWhenChooseSpokenWord(provider.key)
          || this.isDisabledWhenPositiveAnswers(provider.key)
        ) {
          return
        }
        // Facebook/Instagram ミュージックスタンプはFacebookPolicyにマッチしていれば選択可能にする
        this.$set(this.selectedProviders, provider.key,
          !(provider.key === 'musicstamp' && this.isNotMatchFacebookPolicy))
      })
      this.onChangeProviders()
    },
    // 全て解除
    unCheckDatabaseAll() {
      let providers = this.databaseProviders

      if (this.resetting) {
        providers = providers.filter(provider => !this.releasedProviders[provider.key])
      }
      providers.forEach(provider => {
        this.$set(this.selectedProviders, provider.key, false)
      })
      this.onChangeProviders()
      this.validate()
    },
    // 確認画面から戻るボタンクリック
    onEditClicked() {
      if (!this.resetting && this.showConfirmation && this.status >= 1) {
        this.$router.push({ name: `${this.$route.meta.sideMenuId}-step2` })
      } else {
        this.scrollToTop()
        this.showConfirmation = false
      }
    },
    // 同意チェック
    onConfirmed(confirmed) {
      this.confirmed = confirmed
    },
    // handle which content should be displayed in screen
    async showSection(section = '') {
      this.$emit('show-step-area', false)
      if (section === 'confirm-user-data') {
        /* After account confirmation button is clicked
         keep displaying release screen in background until validation check is completed. */
        this.$emit('show-step-area', true)
        this.display = 'release'
        // validate product and track and release information before proceeding sms authentication.
        if (await this.validateRelease()) {
          setTimeout(() => {
            this.reset()
          }, 3000)
        } else {
          this.scrollToTop()
          this.$emit('show-step-area', false)
          this.display = section
          this.reset()
        }
      } else if (section) {
        this.scrollToTop()
        this.display = section
      } else {
        this.display = ''
      }
    },
    // validate if any product or track errors before checking required user details exists
    async validateRelease() {
      const {
        store,
        providers,
        providerSchedules,
        product,
        discs,
      } = JSON.parse(window.sessionStorage.getItem('album'))
      store.publish_date = typeof store.publish_date === 'string' ? moment(store.publish_date).format('YYYY-MM-DD') : store.publish_date.format('YYYY-MM-DD')

      this.$store.commit('incrementLoadingCount')
      this.$store.commit('setIgnoreResetLoading', true)
      this.$store.dispatch('progress/updateLoaded', 0.1)
      this.$store.dispatch(
        'progress/updateDescription',
        '\n\n\n\n',
      )
      this.$store.dispatch(
        'progress/updateText',
        'リリース情報を最終チェック中です。しばらくお待ちください。',
      )
      this.confirmErrors = []
      let hasErrors = false
      // 商品情報を改めてリクエスト
      try {
        if (this.isBtoC) {
          // レーベルが無い場合はアーティスト名を入れる
          if (!product.label_name_ja) {
            product.label_name_ja = product.artist_name
          }
          // B2Cの場合は著作権表記(C)にレーベル名をセット
          product.copyright_c_copyright = product.label_name_ja
          // B2Cの場合は著作権表記(P)にレーベル名をセット
          product.copyright_p_copyright = product.label_name_ja

          // オリジナルリリース年をセット
          const originalReleaseYear = product.original_release_date
            ? parseInt(product.original_release_date.slice(0, 4), 10) : Number.MAX_VALUE
          let copyrightYear
          if (store.simultaneous_release) {
            // オリジナルリリース年が無い場合は配信年をセット
            copyrightYear = parseInt(store.publish_date.slice(0, 4), 10)
          } else {
            // 配信日が個別指定ならば最も過去の年をセット
            const years = []
            let i = 0
            Object.keys(providers).forEach((key) => {
              if (providers[key]) {
                years[i] = parseInt(providerSchedules[key].slice(0, 4), 10)
                i += 1
              }
            }, providers)
            copyrightYear = Math.min(...years)
          }
          product.copyright_c_year = copyrightYear
          product.copyright_p_year = Math.min(originalReleaseYear, copyrightYear)
        }
        if (product.rights_documents) {
          for (let i = 0; i < product.rights_documents.length; i += 1) {
            for (let j = 0; j < product.rights_documents[i].urls.length; j += 1) {
              product.rights_documents[i].urls[j] = product.rights_documents[i].urls[j].path
            }
          }
        }
        await window.axios.put(`/api/client/products/${product.id}?validate_only=1`, product)
      } catch ({ response: productResponse }) {
        if (productResponse.status === 422) {
          hasErrors = true
          this.confirmErrors.push(['商品情報にエラーがあります。'])
          Object.keys(productResponse.data.errors).forEach((key) => {
            this.confirmErrors.push(productResponse.data.errors[key])
          })
          this.$emit('on-error-product', productResponse.data.errors)
          this.scrollToTop()
        }
      }

      try {
        const requestProviders = { ...this.selectedProviders }
        const requestSchedules = { ...this.providerSchedules }
        Object.keys(this.selectedProviders).forEach(providerKey => {
          if (this.selectedProviders[providerKey]
               && this.jointProviderMap[providerKey]) {
            this.jointProviderMap[providerKey].forEach(jointKey => {
              requestProviders[jointKey] = true
              requestSchedules[jointKey] = this.providerSchedules[providerKey]
            })
          }
        })
        await window.axios.post(`/api/client/products/${product.id}/restriction`, {
          providers: { ...requestProviders },
          schedules: { ...requestSchedules },
          publishDate: typeof this.publishDate === 'string' ? this.publishDate : this.publishDate.format('YYYY-MM-DD'),
          simultaneous_release: this.simultaneousRelease,
          validate_only: true,
        })
      } catch ({ response: restrictionResponse }) {
        if (restrictionResponse.status === 422) {
          hasErrors = true
          this.confirmErrors.push(['配信希望日にエラーがあります。'])
          this.restrictionErrors = {}
          Object.keys(restrictionResponse.data.errors).forEach((key) => {
            this.restrictionErrors[key] = restrictionResponse.data.errors[key]
            this.confirmErrors.push(restrictionResponse.data.errors[key])
          })
          this.scrollToTop()
        }
      }

      this.trackCount = 0
      discs.forEach((disc) => { this.trackCount += disc.tracks.length })
      this.trackCount += 1
      let currentCount = 0
      // eslint-disable-next-line no-restricted-syntax
      for (const disc of discs) {
        const discIndex = discs.indexOf(disc)
        // eslint-disable-next-line no-restricted-syntax
        for (const track of disc.tracks) {
          /* eslint-disable no-underscore-dangle, max-len */
          const youtubeRestriction  = track._restrictions.find(restriction => restriction.provider.key === 'youtube')
          if (youtubeRestriction && youtubeRestriction.settings.contents_id_confirm) {
            // YouTubeマネタイズの設問がある場合はtrackにセット
            track.freetrack = youtubeRestriction.settings.contents_id_confirm.freetrack ?? null
            track.use_copyright_free = youtubeRestriction.settings.contents_id_confirm.use_copyright_freek ?? null
            track.has_sold_copyright_free = youtubeRestriction.settings.contents_id_confirm.has_sold_copyright_free ?? null
          } else if (youtubeRestriction) {
            // YouTubeマネタイズの設問が無い場合はtrackにNULLをセットし Validation Errorにする
            track.use_copyright_free = null
            track.has_sold_copyright_free = null
          } else {
            // YouTube自体が無い場合は0をセットしValidationを迂回
            track.use_copyright_free = 0
            track.has_sold_copyright_free = 0
          }
          if ((track.classification === 2 || track.classification === 3) && track.id) {
            track.permission_cover_music = 1
          }
          if (track.rights_documents) {
            for (let i = 0; i < track.rights_documents.length; i += 1) {
              for (let j = 0; j < track.rights_documents[i].urls.length; j += 1) {
                track.rights_documents[i].urls[j] = track.rights_documents[i].urls[j].path
              }
            }
          }
          /* eslint-enable */
          currentCount += 1
          const trackIndex = disc.tracks.indexOf(track)
          // eslint-disable-next-line no-await-in-loop
          await this.$store.dispatch('progress/updateLoaded', (currentCount / this.trackCount) * 100)
          this.$store.dispatch(
            'progress/updateText',
            `楽曲情報を最終チェック中です(${currentCount}/${this.trackCount})。しばらくお待ちください。`,
          )
          try {
            // eslint-disable-next-line no-await-in-loop
            await window.axios.post(`/api/client/products/${product.id}/disc/${discIndex}/track/${trackIndex}?validate_only=1`, track)
          } catch ({ response: trackResponse }) {
            if (trackResponse.status === 422) {
              hasErrors = true
              this.scrollToTop()
              this.confirmErrors.push([`Disk${discIndex + 1}の${trackIndex + 1}曲目に入力エラーがあります。`])
              this.$emit('on-error-track', {
                discIndex,
                trackIndex,
                errors: trackResponse.data.errors,
              })
              Object.keys(trackResponse.data.errors).forEach((key) => {
                this.confirmErrors.push(trackResponse.data.errors[key])
              })
              this.$nextTick(() => {
                window.location.href = `#disc${discIndex}-track${trackIndex}`
              })
            }
          }
          // 楽曲認証
          // eslint-disable-next-line no-await-in-loop
          await window.axios.post(`/api/client/products/${product.id}/disc/${discIndex}/track/${trackIndex}/authenticate-track`, track)
        }
      }
      // SMS認証コード入力画面でリリースエラーが出ないようにため
      try {
        await window.axios.put(`/api/client/products/${product.id}/release?validate_only=1`, {
          store,
          providers,
          discs,
          schedules: providerSchedules,
        })
      } catch ({ response }) {
        if (response.status === 422) {
          hasErrors = true
          Object.keys(response.data.errors).forEach((key) => {
            this.confirmErrors.push(response.data.errors[key])
          })
        }
      }

      return hasErrors
    },
    // 保存するボタン
    async onUpdateRelease() {
      this.$emit('is-confirmed', {
        isConfirmed: true,
      })
      this.$emit('back-from-release', {
        backFromRelease: false,
      })
      // 全てのセッションデータをサーバに送信
      const {
        store,
        providers,
        providerSchedules,
        product,
        discs,
      } = JSON.parse(window.sessionStorage.getItem('album'))
      store.publish_date = typeof store.publish_date === 'string' ? moment(store.publish_date).format('YYYY-MM-DD') : store.publish_date.format('YYYY-MM-DD')
      this.confirmErrors = []
      // eslint-disable-next-line no-await-in-loop
      await this.$store.dispatch('progress/updateLoaded', ((this.trackCount - 0.5) / this.trackCount) * 100)
      this.$store.dispatch(
        'progress/updateText',
        `リリース情報の申請登録中です (${this.trackCount}/${this.trackCount})。`,
      )
      this.$store.dispatch(
        'progress/updateDescription',
        '登録には少し時間がかかりますので、\nそのまま画面を閉じずお待ちください。\n※アルバムなど楽曲数が多い場合、\n数分かかる場合がございますが、\n完了画面が表示されるまでお待ちください。',
      )
      try {
        const requestProviders = { ...providers }
        const requestSchedules = { ...providerSchedules }
        Object.keys(this.selectedProviders).forEach(providerKey => {
          if (this.selectedProviders[providerKey]
               && this.jointProviderMap[providerKey]) {
            this.jointProviderMap[providerKey].forEach(jointKey => {
              requestProviders[jointKey] = true
              requestSchedules[jointKey] = this.providerSchedules[providerKey]
            })
          }
        })
        await window.axios.put(`/api/client/products/${product.id}/release`, {
          store,
          providers: requestProviders,
          discs,
          schedules: requestSchedules,
        })
        if (this.isBtoB || this.isSubscriber || this.hasSettled === 1 || this.isMate) {
          await this.$router.push({ name: `${this.$route.meta.sideMenuId}-completed` })
        } else {
          await this.$router.push({ name: `${this.$route.meta.sideMenuId}-step5` })
        }
      } catch ({ response }) {
        if (response.status === 422) {
          Object.keys(response.data.errors).forEach((key) => {
            this.confirmErrors.push(response.data.errors[key])
          })
          /* show release screen with errors if in case
           release related errors occur after successful sms authentication. */
          this.displayReleaseErrors()
        }
      }
      this.reset()
    },
    // 保存するボタン
    async onReleaseClicked() {
      this.$emit('is-confirmed', {
        isConfirmed: true,
      })
      this.$emit('back-from-release', {
        backFromRelease: false,
      })
      const hasErrors = await this.validateRelease()
      if (hasErrors) {
        setTimeout(() => {
          this.reset()
        }, 3000)
        return
      }
      await this.onUpdateRelease()
    },
    onChangeSinglePrice() {
      const release = JSON.parse(window.sessionStorage.getItem('album'))
      let total = 0
      release.discs.forEach((disc) => {
        total += disc.tracks.length
      })
      const maxPrice = this.singlePrice * total
      this.availablePackagePrices = this.packagePrices.filter(price => price <= maxPrice)
      if (total >= 11) {
        this.availablePackagePrices.splice(0, 3)
      } else if (total >= 8) {
        this.availablePackagePrices.splice(0, 2)
      } else if (total >= 5) {
        this.availablePackagePrices.splice(0, 1)
      }
      if (!this.resetting) {
        this.packagePrice = window._.last(this.availablePackagePrices) || ''
      }
      this.validate()
    },
    onChangePackagePrice() {
      this.validate()
    },

    onChangeProviders() {
      const providers = window._.orderBy(
        this.providers,
        'min_release_date',
        'desc',
      )
      // 全てのDSPの一番遅い配信日時を最短リリース日とする。
      this.minDate = moment(new Date(window._.first(providers).min_release_date))
      if (this.status === 0 && moment(this.publishDate).isBefore(this.minDate)) {
        this.publishDate = this.minDate
      }
      // format publishDate to show in date-picker (simultaneous release)
      this.publishDate = moment(this.publishDate).format('YYYY-MM-DD')
      this.validate()
    },
    validate() {
      this.storeErrors = []
      this.priceErrors = []
      if (this.$store.state.auth.permission === 10) {
        return
      }
      const selectedProviders = { ...this.selectedProviders }
      if (this.resetting) {
        Object.keys(this.releasedProviders).forEach(providerKey => {
          delete selectedProviders[providerKey]
        })
      }
      if (this.downloadEnabled) {
        const hasDownload = this.downloadProviders.filter(provider => (
          this.selectedProviders[provider.key]
        )).length > 0
        if (!hasDownload) {
          this.storeErrors.push('ダウンロード配信先ストアを選択してください。')
        }
        if (!this.singlePrice) {
          this.storeErrors.push('単曲のダウンロード価格を選択してください。')
        }
        if (this.type === 'album' && !this.packagePrice) {
          this.storeErrors.push('パッケージのダウンロード価格を選択してください。')
        }
        if (this.type === 'album' && this.isHiRes && !this.hiResPackagePrice) {
          this.storeErrors.push('パッケージ（ハイレゾ版）のダウンロード価格を選択してください。')
        }
      }

      // ハイレゾの単曲価格チェック
      if (this.hiResSinglePrice
        && (this.hiResSinglePrice < 150 || this.hiResSinglePrice > 550)) {
        this.priceErrors.push('単曲のダウンロード価格（ハイレゾ版）は150〜550円で設定して下さい。')
      }

      // ハイレゾのパッケージ価格チェック
      if (this.hiResPackagePrice) {
        if (this.hiResPackagePrice < 300 || this.hiResPackagePrice > 5093) {
          this.priceErrors.push('パッケージのダウンロード価格（ハイレゾ版）は300〜5093円で設定して下さい。')
        }

        if (this.hiResSinglePrice * this.totalTracks < this.hiResPackagePrice) {
          this.priceErrors.push('パッケージのダウンロード価格（ハイレゾ版）は単曲価格 × 曲数の価格以下に設定して下さい。')
        }
      }

      if (this.isHiRes && (this.singlePrice || this.hiResSinglePrice)) {
        if (!this.singlePrice || !this.hiResSinglePrice) {
          this.storeErrors.push('ダウンロード価格とダウンロード価格（ハイレゾ版）両方を入力してください。')
        }
      }
      const hasSubscription = this.subscriptionProviders.filter(provider => (
        selectedProviders[provider.key]
      )).length > 0
      const hasDatabaseService = this.databaseProviders.filter(provider => (
        selectedProviders[provider.key]
      )).length > 0
      const hasDownloadService = this.downloadProviders.filter(provider => (
        selectedProviders[provider.key]
      )).length > 0
      if (!hasDownloadService && !hasSubscription && !hasDatabaseService) {
        this.storeErrors.push('配信先ストアを選択してください。')
      }
      if (!this.simultaneousRelease) {
        const providerPatterns = [
          {
            stKey: 'amazon',
            dlKey: 'amazon-dl',
            stName: '「Amazon Music Unlimited」',
            dlName: '「Amazonデジタルミュージックストア」',
          },
          {
            stKey: '7digital',
            dlkey: '7digital-dl',
            stName: '「7digital」の サブスクリプション配信',
            dlName: 'ダウンロード配信 ',
          },
          {
            stKey: 'beatport',
            dlKey: 'beatport-dl',
            stName: '「Beatport」の サブスクリプション配信 ',
            dlName: 'ダウンロード配信 ',
          },
          {
            stKey: 'uma',
            dlKey: 'uma-dl',
            stName: '「United Media Agency」の サブスクリプション配信',
            dlName: 'ダウンロード配信',
          },
          {
            stKey: 'tencent',
            dlKey: 'tencent-dl',
            stName: '「Tencent」の サブスクリプション配信',
            dlName: 'ダウンロード配信',
          },
          {
            stKey: 'melon',
            dlKey: 'melon-dl',
            stName: '「MelOn」の サブスクリプション配信',
            dlName: 'ダウンロード配信 ',
          },
          {
            stKey: 'bugs',
            dlKey: 'bugs-dl',
            stName: '「Bugs! / Naver VIBE / Genie」の サブスクリプション配信',
            dlName: 'ダウンロード配信',
          },
          {
            stKey: 'qobuz',
            dlKey: 'qobuz-dl',
            stName: '「Qobuz」の サブスクリプション配信',
            dlName: 'ダウンロード配信',
          },
        ]
        for (let index = 0; index < providerPatterns.length; index += 1) {
          const provider = providerPatterns[index]
          if (selectedProviders[provider.stKey] && selectedProviders[provider.dlKey]) {
            if (!moment(this.providerSchedules[provider.stKey]).startOf('day')
              .isSame(moment(this.providerSchedules[provider.dlKey]).startOf('day'))) {
              this.storeErrors.push(
                `${provider.stName}と${provider.dlName}の配信希望日は同じ日に設定する必要があります`,
              )
            }
          }
        }
      }
      if (this.status === 2) {
        // FUGA経由DSPでストリーミング, Dlの片方が既に配信されている場合のストア追加の対応
        if (selectedProviders.amazon || selectedProviders['amazon-dl']) {
          this.onSetUnReleasedProviderSchedules(['amazon', 'amazon-dl'])
        }
        if (selectedProviders.beatport || selectedProviders['beatport-dl']) {
          this.onSetUnReleasedProviderSchedules(['beatport', 'beatport-dl'])
        }
        if (selectedProviders['7digital'] || selectedProviders['7digital-dl']) {
          this.onSetUnReleasedProviderSchedules(['7digital', '7digital-dl'])
        }
        if (selectedProviders.uma || selectedProviders['uma-dl']) {
          this.onSetUnReleasedProviderSchedules(['uma', 'uma-dl'])
        }
      }
    },
    onSetUnReleasedProviderSchedules(pairProviders) {
      Object.keys(this.unReleasedPairProviders).forEach(provider => {
        const releasedPair = pairProviders.find(p => provider !== p)
        const releasedPairSchedule = moment(this.providerSchedules[releasedPair])
        // ストリーミング or Dlが既に配信をされている場合は'DSPの最短配信日'が入る
        // ストリーミング or Dlが先にリリース申請されていて、配信日がまだの場合は'同じ配信日'が入る。
        this.$set(
          this.providerSchedules,
          pairProviders.find(p => provider === p),
          moment(releasedPairSchedule).isBefore(moment())
            ? moment(this.providerMinSchedules[provider]).format('YYYY-MM-DD')
            : releasedPairSchedule.format('YYYY-MM-DD'),
        )
      })
    },
    onReleaseDateClicked(provider, providerKey) {
      this.selectedProviderKey = providerKey
      this.releaseDateModalProvider = provider

      const hasProviderSchedules = this.hasProviderSchedules(this.selectedProviderKey)
      this.discs.map((disc, index) => {
        disc.tracks.map((track, index2) => {
          if (!track.restrictions) {
            this.$set(this.discs[index].tracks[index2], 'restrictions', {})
          }
          if (!track.restrictions[providerKey] || !hasProviderSchedules) {
            this.$set(
              this.discs[index].tracks[index2].restrictions,
              providerKey,
              this.providerSchedules[providerKey],
            )
          }
          return track
        })
        return disc
      })
      this.releaseDateModalIsShown = true
    },
    // 曲ごとの設定があるかどうか。
    hasProviderSchedules(providerKey) {
      let prevDate = ''
      const result = this.discs.filter(disc => disc.tracks.filter(track => {
        if (!track.restrictions || !track.restrictions[providerKey]) {
          return false
        }
        const releaseDate = moment(new Date(track.restrictions[providerKey])).format('YYYY-MM-DD')
        const result2 = prevDate && releaseDate !== prevDate
        prevDate = releaseDate
        return result2
      }).length > 0)
      return result.length > 0
    },
    onReleaseDateModalClosed() {
      if (!this.hasProviderSchedules(this.selectedProviderKey)) {
        const firstTrack = window._.first(window._.first(this.discs).tracks)
        const releaseDate = firstTrack.restrictions[this.selectedProviderKey]
        this.providerSchedules[this.selectedProviderKey] = releaseDate
      }
      this.releaseDateModalIsShown = false
    },
    // 詳細確認画面からの戻りボタンクリック
    onReturnClicked() {
      this.scrollToTop()
      if (this.status === 2 && this.resetting) {
        this.$router.push({ name: 'release-detail' })
        return
      }
      this.$router.push({ name: 'release-index' })
    },
    onReturn2Clicked() {
      this.$router.push({ name: `${this.$route.meta.sideMenuId}-step2` })
    },
    // ストア再設定ボタンクリック
    onStoreSettingClicked() {
      this.scrollToTop()
      this.$router.push({ name: `${this.$route.meta.sideMenuId}-${this.type}-step3` })
    },
    /**
     * disc情報の中のYoutube配信に関して指定したキーの状態を調べる
     * @param discs
     * @param {String} checkKey - 'contents_enabled' or 'contents_id_enabled'
     * @param {Number} checkState - 1 or 0
     * @returns {boolean}
     */
    checkTracksYoutubeState(discs, checkKey, checkState) {
      return undefined !== discs.find(
        // eslint-disable-next-line no-underscore-dangle
        disc => disc.tracks.find(track => track._restrictions?.find(
          restriction => restriction.provider.key === 'youtube'
            && this.checkRestrictionProperty(restriction.settings, checkKey) === checkState,
        )),
      )
    },
    // narasu導入以前の楽曲でYoutubeのcontentsID周りにプロパティがない場合に1を返す
    checkRestrictionProperty(settings, checkKey) {
      if (!Object.prototype.hasOwnProperty.call(settings, checkKey)) return 1
      return settings[checkKey]
    },
    onCheckUnReleasedPairProviders() {
      if (this.status !== 2) return
      // FUGA経由DSPでストリーミング, Dlの片方をすでに配信してる場合のストア追加対応
      Object.keys(this.releasedProviders).forEach(provider => {
        if (
          (provider === 'amazon' && !('amazon-dl' in this.releasedProviders))
          || (provider === 'amazon-dl' && !('amazon' in this.releasedProviders))
        ) {
          // (amazon, amazon-dl)の配信されてないストアを検出
          this.$set(
            this.unReleasedPairProviders,
            ['amazon', 'amazon-dl'].find(i => i !== provider),
            true,
          )
        }
        if (
          (provider === 'beatport' && !('beatport-dl' in this.releasedProviders))
          || (provider === 'beatport-dl' && !('beatport' in this.releasedProviders))
        ) {
          // (beatport, beatport-dl)の配信されてないストアを検出
          this.$set(
            this.unReleasedPairProviders,
            ['beatport', 'beatport-dl'].find(i => i !== provider),
            true,
          )
        }
        if (
          (provider === '7digital' && !('7digital-dl' in this.releasedProviders))
          || (provider === '7digital-dl' && !('7digital' in this.releasedProviders))
        ) {
          // (7digital, 7digital-dl)の配信されてないストアを検出
          this.$set(
            this.unReleasedPairProviders,
            ['7digital', '7digital-dl'].find(i => i !== provider),
            true,
          )
        }
        if (
          (provider === 'uma' && !('uma-dl' in this.releasedProviders)) || (provider === 'uma-dl' && !('uma' in this.releasedProviders))
        ) {
          // (uma, uma-dl)の配信されてないストアを検出
          this.$set(
            this.unReleasedPairProviders,
            ['uma', 'uma-dl'].find(i => i !== provider),
            true,
          )
        }
      })
    },
    getPairProviderName(key) {
      if (key === 'amazon' || key === 'amazon-dl') {
        const pairProvider = this.providers
          .find(p => p.key === ['amazon', 'amazon-dl'].find(i => i !== key))
        return pairProvider.display_name
      }
      if (key === 'beatport' || key === 'beatport-dl') {
        const pairProvider = this.providers
          .find(p => p.key === ['beatport', 'beatport-dl'].find(i => i !== key))
        return pairProvider.display_name
      }
      if (key === '7digital' || key === '7digital-dl') {
        const pairProvider = this.providers
          .find(p => p.key === ['7digital', '7digital-dl'].find(i => i !== key))
        return pairProvider.display_name
      }
      if (key === 'uma' || key === 'uma-dl') {
        const pairProvider = this.providers
          .find(p => p.key === ['uma', 'uma-dl'].find(i => i !== key))
        return pairProvider.name
      }
      return false
    },
    onCheckBlankScheduleProviders() {
      // 検出対象のDSP
      const {
        amazon,
        'amazon-dl': amazonDl,
        uma,
        'uma-dl': umaDl,
        beatport,
        'beatport-dl': beatportDl,
        '7digital': sevenDigital,
        '7digital-dl': sevenDigitalDl,
      } = this.releasedProviders
      if (amazon && amazonDl) {
        this.onDetectBlankScheduleProviders(['amazon', 'amazon-dl'])
      }
      if (uma && umaDl) {
        this.onDetectBlankScheduleProviders(['uma', 'uma-dl'])
      }
      if (sevenDigital && sevenDigitalDl) {
        this.onDetectBlankScheduleProviders(['7digital', '7digital-dl'])
      }
      if (beatport && beatportDl) {
        this.onDetectBlankScheduleProviders(['beatport', 'beatport-dl'])
      }
    },
    onDetectBlankScheduleProviders(providers) {
      // 配信日を表示させないDSPを検出(FUGA経由のamazon, uma, etc)
      const { _restrictions: restrictions } = this.discs[0].tracks[0]

      const restriction = restrictions.find((r) => r.provider.key === providers[0])
      const restrictionDl = restrictions.find((r) => r.provider.key === providers[1])
      const requestedDate = moment(restriction.requested_at)
      const dlRequestedDate = moment(restrictionDl.requested_at)
      if (!requestedDate.isSame(dlRequestedDate)
        && restriction.start !== restrictionDl.start) {
        if (requestedDate.isBefore(dlRequestedDate)) {
          this.$set(this.blankScheduleProviders, restrictionDl.provider.key, true)
        } else if (dlRequestedDate.isBefore(requestedDate)) {
          this.$set(this.blankScheduleProviders, restriction.provider.key, true)
        }
      }
    },
    isDisabledWhenChooseSpokenWord(providerKey) {
      return this.isSpokenWord && this.disabledProvidersSpokenWord.includes(providerKey)
    },
    isDisabledWhenChooseClassical(providerKey) {
      return this.isClassical && providerKey === 'soundcloud'
    },
    isDisabledWhenChooseVariousArtist(providerKey) {
      return this.isVariousArtist && providerKey === 'soundcloud'
    },
    isDisabledWhenChoosePublicDomain(providerKey) {
      if (providerKey === 'soundcloud' || providerKey === 'tidal') {
        for (let i = 0; i < this.discs.length; i += 1) {
          if (this.discs[i].tracks.some((track => track.classification === 4))) {
            return true
          }
        }
      } else {
        return false
      }
      return false
    },
    isDisabledWhenHasLongTrack(providerKey) {
      return this.hasLongTrack && providerKey === 'soundcloud'
    },
    isDisabledWhenPositiveAnswers(providerKey) {
      if (providerKey === 'soundcloud' || providerKey === 'musicstamp') {
        for (let i = 0; i < this.discs.length; i += 1) {
          if (this.discs[i].tracks.some((
            track => track.freetrack === 1
            || track.has_sold_copyright_free === 1
            || track.use_copyright_free === 1
          ))) {
            return true
          }
        }
      }
      return false
    },
    reset() {
      this.$store.commit('setIgnoreResetLoading', false)
      this.$store.commit('clearLoadingCount')
      this.$store.dispatch('progress/reset')
    },
    displayReleaseErrors() {
      this.$emit('show-step-area', true)
      this.showConfirmation = true
      this.confirmed = false
      this.isErrorFromSecurityCodeScreen = true
      this.display = 'release'
    },
    // 全ストア同日配信の最短日で指定する選択した場合
    disableDatesForSimultaneousRelease(date) {
      return this.disabledDates(date, this.minDate)
    },
    // ストア別の最短配信日で指定選択した場合
    disableDatesForNonSimultaneousRelease(date, minDate) {
      return this.disabledDates(date, minDate)
    },
    disabledDates(date, minDate) {
      const dateUnSelectable = moment(minDate).subtract(1, 'd')
      const lastDate = this.getSubmitDate().add(1, 'year')
      return date < dateUnSelectable || date > lastDate
    },
    // calculate date range
    setDateRange() {
      // start date
      let date = this.getSubmitDate()
      const dateArray = []
      // end date
      const lastDate = date.clone().add(1, 'year')
      while (date <= lastDate) {
        dateArray.push(date.format('YYYY-MM-DD'))
        date = date.add(1, 'd')
      }
      this.dateRange = dateArray
      this.setBackgroundToDate(dateArray)
    },
    // apply background color to submit possible dates
    setBackgroundToDate(dateArray) {
      setTimeout(() => {
        this.dateRange.forEach(date => {
          const element = document.querySelector(`[title="${date}"]`)
          if (dateArray.includes(date) && element) {
            element.style.background = '#FEB1B3'
            if (element.classList.contains('active')) {
              // blue color for already selected date
              element.style.background = '#1484E8'
            }
          }
        })
      }, 50)
    },
    onCalenderChange() {
      setTimeout(() => {
        const cellE = document.querySelectorAll('[class^="cell"]')
        const visibleDateArray = []
        cellE.forEach(e => {
          visibleDateArray.push(e.attributes[1].value)
          e.style.background = 'white'
        })
        const activeCell = document.querySelector('[class="cell active"]')
        if (activeCell) {
          activeCell.style.background = '#1484E8'
        }
        this.setBackgroundToDate(visibleDateArray)
      }, 10)
    },
    // Get the date by adding the specified submit days to the current date.
    getSubmitDate() {
      return moment().add(this.submitDay, 'd')
    },
  },
}
</script>

<style scoped>
.form-group-disabled {
  opacity: 0.4;
  pointer-events: none;
}
.store-error {
  text-align: center;
  padding-top: 5vmin;
  padding-bottom: 5vmin;
  color: #ef3c3c;
}
.price-error {
  font-size: 13px;
  margin-top: 10px;
  color: #ef3c3c;
}
.min-release-description {
  color: gray;
}
.min-release-select {
  margin-left: 32px;
  zoom: 80%;
  display: flex;
  flex-wrap: wrap;
}
.min-release-select .c-btn {
  height: 44px;
  width: 200px;
  background-color: #bcb7b3;
  min-width: 200px;
  line-height: 44px;
  padding: 1px 10px 1px 10px;
}
.min-release-select .c-btn-confirm {
  width: 280px;
  min-width: 280px;
  background-color: #ff6c00;
}
@media screen and (min-width: 896px) {
  .min-release-select .date-select {
    flex-basis: 65%;
  }
  .min-release-select .c-btn {
    flex-basis: 35%;
  }
  .simultaneous-release{
    align-items: stretch;
  }
  .simultaneous-release__select-box{
    margin-top:10px;
  }

  .simultaneous-release .radio {
    width: 50%;
  }
}
@media screen and (max-width: 895px) {
  .min-release-select .date-select {
    flex-basis: 100%;
  }
  .min-release-select .c-btn {
    margin-top: 5px;
    flex-basis: 65%;
  }
  .simultaneous-release{
  }
  .simultaneous-release__select-box{
    margin-top:10px;
  }
}

.slide-enter-active, .slide-leave-active {
  max-height: 500px;
  transition: max-height 0.25s ease-in, opacity 0.25s ease-in;
}
.slide-enter, .slide-leave-to {
  opacity: 0;
  max-height: 0;
  transition: max-height 0.15s ease-out, opacity 0.15s ease-out;
  overflow: hidden;
}
.form-item-text--inline {
  margin-top: 5.5px;
}
INPUT:disabled {
  background-color: gray;
  filter: opacity(30%);
}
.price-input-form-disabled {
  background-color: #e2e2e2!important;
  filter: opacity(80%)!important;
}

.check-list {
  align-items: center;
}

.list-flex {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
}

.check-list__node-img {
  margin: 10px 0;
  width: auto;
}

.check-list__node > label {
  font-weight: bold;
}

.list-flex div {
  display: flex;
  align-items: center;
}

.form-pad {
  padding: 24px 0;
}

.form-item textarea {
  margin: 4px 0;
}

.form-item .form-control {
  width: 100%;
}
.form-group {
  border-bottom: none;
}

.control-label {
  font-weight: bold;
}

.release-jacket-area {
  align-items: center;
}

.release-jacket-area__upload-box {
  padding: 30px 25px;
  width: auto;
  margin: auto;
  margin-left: 60px;
}

.release-jacket-area__pic {
  width: 240px;
  height: 240px;
  background: #d0d0d0 url("../../../../../../public/img/common/icon-image_white.png")
  no-repeat center center;
  background-size: 73px auto;
}

.release-jacket-area__pic img {
  top: 50%;
  left: 0;
  height: auto;
  transform: none;
  position: initial;
}

@media screen and (min-width: 896px) {
  .preview-image {
    width: 240px;
    height: 240px;
  }
}

@media screen and (max-width: 895px) {
  .preview-image {
    width: 50vw;
    height: 50vw;
  }
}

.release-jacket-area__pic-rect {
  height: 120px;
}
.form--error .error-text {
  text-align: center;
}

.mt-5{
  margin-top: 5px;
}
.mb-10{
  margin-bottom: 10px;
}
.mr-30{
  margin-right: 30px;
}
.hide-display {
  display: none;
}
</style>
